// MySelectFromItems.tsx
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import MyBox from "./MyBox";
import { useState, useRef, useEffect } from "react";

export type SelectItem<T = string> = {
  label?: string;
  name?: string;
  value: T;
  details?: string;
  icon?: React.ReactNode;
  color?: string;
  description?: string;
  disabled?: boolean;
};

interface MySelectFromItemsProps<T = string> {
  label?: string;
  items: SelectItem<T>[];
  value: T | null;
  onChange: (value: T) => void;
  style?: string;
  labelStyle?: string;
  className?: string;
  mode?: 'cards' | 'dropdown' | 'chips' | 'list' | 'radio' | 'segmented' | 'compact';
  size?: 'sm' | 'md' | 'lg';
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  multiple?: boolean;
  maxDisplay?: number;
  disabled?: boolean;
  error?: string;
  helperText?: string;
}

export default function MySelectFromItems<T>({
  label,
  items,
  value,
  onChange,
  style = "p-2",
  labelStyle = "font-medium text-[12px]",
  className = "",
  mode = 'cards',
  size = 'md',
  placeholder = "Select an option",
  searchable = false,
  clearable = false,
  multiple = false,
  maxDisplay = 3,
  disabled = false,
  error,
  helperText,
}: MySelectFromItemsProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get selected items
  const selectedItems = multiple 
    ? (Array.isArray(value) ? value : []) 
    : (value ? [value] : []);
  
  const selectedItemObjects = selectedItems
    .map(v => items.find(item => item.value === v))
    .filter(Boolean) as SelectItem<T>[];

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (itemValue: T) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(itemValue)
        ? currentValues.filter(v => v !== itemValue)
        : [...currentValues, itemValue];
      onChange(newValues as any);
    } else {
      onChange(itemValue);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      onChange([] as any);
    } else {
      onChange(null as any);
    }
  };

  const removeSelected = (itemValue: T, e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      onChange(currentValues.filter(v => v !== itemValue) as any);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'text-xs p-1.5';
      case 'lg': return 'text-base p-3';
      default: return 'text-sm p-2';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm': return 14;
      case 'lg': return 20;
      default: return 16;
    }
  };

  // Render different modes
  const renderCardsMode = () => (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        const customColor = item.color;

        return (
          <button
            key={String(item.value ?? item.name)}
            type="button"
            disabled={disabled || item.disabled}
            onClick={() => handleSelect(item.value)}
            className={`
              flex items-center justify-between gap-3 rounded-lg border 
              ${getSizeClasses()} text-left transition-all duration-200
              ${active
                ? "border-gray-900 dark:border-gray-300 bg-gray-50 dark:bg-gray-700 ring-1 ring-gray-900 dark:ring-gray-300"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              }
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
            style={customColor && active ? { borderColor: customColor } : {}}
          >
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                {item?.icon && <div className="text-gray-600 dark:text-gray-400">{item.icon}</div>}
                <div>
                  <div className={`${labelStyle} ${active ? 'font-semibold text-green-700 dark:text-white' : 'text-gray-600 dark:text-gray-200'}`}>
                    {item.label || item.name}
                  </div>
                  {item.details && (
                    <p className="text-[10px] text-orange-500 dark:text-orange-400 mt-0.5">{item.details}</p>
                  )}
                </div>
              </div>
              {item.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.description}</p>
              )}
            </div>
            <div className={`
              w-5 h-5 rounded-full flex items-center justify-center transition-all flex-shrink-0
              ${active
                ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm"
                : "border border-gray-300 dark:border-gray-600 dark:text-gray-200 bg-white dark:bg-gray-800"
              }
            `}>
              {active && <Check size={getIconSize()} strokeWidth={2.5} />}
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderChipsMode = () => (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        
        return (
          <button
            key={String(item.value ?? item.name)}
            type="button"
            disabled={disabled || item.disabled}
            onClick={() => handleSelect(item.value)}
            className={`
              inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium
              transition-all duration-200
              ${active
                ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-400 border-gray-900 dark:border-gray-100"
                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
              }
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {item?.icon && <span className="text-inherit">{item.icon}</span>}
            {item.label || item.name}
          </button>
        );
      })}
    </div>
  );

  const renderListMode = () => (
    <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {items.map((item, index) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        
        return (
          <button
            key={String(item.value ?? item.name)}
            type="button"
            disabled={disabled || item.disabled}
            onClick={() => handleSelect(item.value)}
            className={`
              flex items-center justify-between px-4 py-2.5 transition-all duration-150
              ${active
                ? "bg-gray-50 dark:bg-gray-700/50"
                : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
              }
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${index !== items.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""}
            `}
          >
            <div className="flex items-center gap-3">
              {item?.icon && <div className="text-gray-500 dark:text-gray-400">{item.icon}</div>}
              <div>
                <div className={`text-sm ${active ? 'font-semibold' : ''}`}>
                  {item.label || item.name}
                </div>
                {item.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                )}
              </div>
            </div>
            {active && <Check size={16} className="text-gray-900 dark:text-white" />}
          </button>
        );
      })}
    </div>
  );

  const renderRadioMode = () => (
    <div className="space-y-2">
      {items.map((item) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        
        return (
          <label
            key={String(item.value ?? item.name)}
            className={`
              flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all
              ${active ? "bg-gray-50 dark:bg-gray-700/50" : "hover:bg-gray-50 dark:hover:bg-gray-700/30"}
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            <input
              type={multiple ? "checkbox" : "radio"}
              name={label}
              checked={active}
              disabled={disabled || item.disabled}
              onChange={() => handleSelect(item.value)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {item?.icon && <div className="text-gray-500 dark:text-gray-400">{item.icon}</div>}
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.label || item.name}
                </span>
              </div>
              {item.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );

  const renderSegmentedMode = () => (
    <div className="flex flex-wrap gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
      {items.map((item) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        
        return (
          <button
            key={String(item.value ?? item.name)}
            type="button"
            disabled={disabled || item.disabled}
            onClick={() => handleSelect(item.value)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium
              transition-all duration-200
              ${active
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {item?.icon && <span className="text-inherit">{item.icon}</span>}
            {item.label || item.name}
          </button>
        );
      })}
    </div>
  );

  const renderCompactMode = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
      {items.map((item) => {
        const active = !multiple ? value === item.value : selectedItems.includes(item.value);
        
        return (
          <button
            key={String(item.value ?? item.name)}
            type="button"
            disabled={disabled || item.disabled}
            onClick={() => handleSelect(item.value)}
            className={`
              text-xs py-1.5 px-2 rounded-md text-center transition-all duration-200
              ${active
                ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-400 font-medium"
                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }
              ${(disabled || item.disabled) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            {item.label || item.name}
          </button>
        );
      })}
    </div>
  );

  const renderDropdownMode = () => (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2
          bg-white dark:bg-gray-800
          border rounded-lg
          ${getSizeClasses()}
          transition-all duration-200
          ${error 
            ? 'border-red-500 dark:border-red-400 focus:ring-red-500' 
            : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500'
          }
          ${disabled 
            ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900' 
            : 'hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer'
          }
          focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800
        `}
      >
        <div className="flex-1 flex items-center gap-2">
          {selectedItemObjects.length > 0 ? (
            multiple ? (
              <div className="flex flex-wrap gap-1">
                {selectedItemObjects.slice(0, maxDisplay).map((item, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md text-xs">
                    {item.label || item.name}
                    {clearable && (
                      <X
                        size={12}
                        className="cursor-pointer hover:text-red-500"
                        onClick={(e) => removeSelected(item.value, e)}
                      />
                    )}
                  </span>
                ))}
                {selectedItemObjects.length > maxDisplay && (
                  <span className="text-xs text-gray-500">+{selectedItemObjects.length - maxDisplay}</span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {selectedItemObjects[0]?.icon && (
                  <span className="text-gray-500 dark:text-gray-400">{selectedItemObjects[0].icon}</span>
                )}
                <span className="text-gray-900 dark:text-white">
                  {selectedItemObjects[0]?.label || selectedItemObjects[0]?.name || placeholder}
                </span>
              </div>
            )
          ) : (
            <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
          )}
        </div>
        {clearable && selectedItemObjects.length > 0 && (
          <X
            size={getIconSize()}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={handleClear}
          />
        )}
        {isOpen ? <ChevronUp size={getIconSize()} /> : <ChevronDown size={getIconSize()} />}
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900/50 overflow-hidden animate-slide-down">
          {searchable && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="max-h-60 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                No options found
              </div>
            ) : (
              filteredItems.map((item) => {
                const active = !multiple ? value === item.value : selectedItems.includes(item.value);
                
                return (
                  <button
                    key={String(item.value ?? item.name)}
                    type="button"
                    disabled={item.disabled}
                    onClick={() => handleSelect(item.value)}
                    className={`
                      w-full px-4 py-2.5 text-left flex items-center gap-3
                      transition-colors duration-150
                      ${active
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }
                      ${item.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    `}
                  >
                    {item.icon && <span className="text-gray-500 dark:text-gray-400">{item.icon}</span>}
                    <div className="flex-1">
                      <div className="text-sm font-medium">{item.label || item.name}</div>
                      {item.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                      )}
                    </div>
                    {active && <Check size={16} className="flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Main render based on mode
  const renderContent = () => {
    switch (mode) {
      case 'dropdown':
        return renderDropdownMode();
      case 'chips':
        return renderChipsMode();
      case 'list':
        return renderListMode();
      case 'radio':
        return renderRadioMode();
      case 'segmented':
        return renderSegmentedMode();
      case 'compact':
        return renderCompactMode();
      default:
        return renderCardsMode();
    }
  };

  if (mode === 'dropdown') {
    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {label}
          </label>
        )}
        {renderContent()}
        {helperText && !error && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
        {error && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    );
  }

  return (
    <MyBox topText={label} className={className}>
      {renderContent()}
      {helperText && !error && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </MyBox>
  );
}




// // Cards mode (default) - Great for visual options with icons
// <MySelectFromItems
//   label="Property Type"
//   items={propertyTypes}
//   value={selectedType}
//   onChange={setSelectedType}
//   mode="cards"
// />

// // Dropdown mode - Best for many options
// <MySelectFromItems
//   label="Country"
//   items={countries}
//   value={selectedCountry}
//   onChange={setSelectedCountry}
//   mode="dropdown"
//   searchable
//   clearable
//   placeholder="Select a country"
// />

// // Chips mode - Compact, great for filters
// <MySelectFromItems
//   label="Amenities"
//   items={amenities}
//   value={selectedAmenities}
//   onChange={setSelectedAmenities}
//   mode="chips"
//   multiple
// />

// // List mode - Clean vertical list
// <MySelectFromItems
//   label="Payment Method"
//   items={paymentMethods}
//   value={selectedMethod}
//   onChange={setSelectedMethod}
//   mode="list"
// />

// // Radio mode - Traditional form style
// <MySelectFromItems
//   label="Gender"
//   items={genders}
//   value={selectedGender}
//   onChange={setSelectedGender}
//   mode="radio"
// />

// // Segmented mode - Great for 2-4 options
// <MySelectFromItems
//   items={viewModes}
//   value={viewMode}
//   onChange={setViewMode}
//   mode="segmented"
//   size="sm"
// />

// // Compact mode - Space-efficient grid
// <MySelectFromItems
//   items={colors}
//   value={selectedColor}
//   onChange={setSelectedColor}
//   mode="compact"
// />

// // Multiple selection with dropdown
// <MySelectFromItems
//   label="Interests"
//   items={interests}
//   value={selectedInterests}
//   onChange={setSelectedInterests}
//   mode="dropdown"
//   multiple
//   searchable
//   clearable
//   maxDisplay={3}
// />

// // With error state
// <MySelectFromItems
//   label="Required Field"
//   items={options}
//   value={value}
//   onChange={setValue}
//   error="This field is required"
// />

// // Disabled state
// <MySelectFromItems
//   label="Disabled Select"
//   items={options}
//   value={value}
//   onChange={setValue}
//   disabled
// />